// ui/annotation-logic.js: pure helpers for bot annotation lifecycle
// (M2.c-9, Pillar 5).
//
// Plan-m2 §M2.c-9: "Fades in over 200ms, holds for 3s, fades out over
// 300ms." Total 3500ms. Reduced-motion skips the fades, alpha snaps
// to 1 for the hold window then to 0 at the end.

export const PHASES_MS = Object.freeze({
  fade_in:  200,
  hold:     3000,
  fade_out: 300,
});

export const TOTAL_MS = PHASES_MS.fade_in + PHASES_MS.hold + PHASES_MS.fade_out;

/**
 * Pillar 5 leak guard. Bot annotations must NEVER contain raw card IDs
 * from other seats' hands, that's the M2.b-4 invariant promoted into
 * the GameRunner. This regex matches the canonical 2-char card ID
 * (rank ∈ 9,T,J,Q,K,A; suit ∈ S,H,D,C). The match must be a standalone
 * token (bounded by word break or punctuation) to avoid false-positives
 * like "AS" inside "AS soon as..." or the suit name "Hearts".
 */
const CARD_ID_RE = /\b([9TJQKA])([SHDC])\b/;

/**
 * Returns true when `text` contains a card-ID pattern. Used by tests
 * and (paranoia) the production push path.
 *
 * @param {string|null|undefined} text
 * @returns {boolean}
 */
export function containsCardIdLeak(text) {
  if (typeof text !== 'string' || !text) return false;
  return CARD_ID_RE.test(text);
}

/**
 * Compute alpha (0..1) for an annotation at `elapsed_ms` since push.
 *
 *   t <      0  → 0   (defensive, shouldn't happen)
 *   t <    200  → fade in (linear 0→1)
 *   t <   3200  → hold (1.0)
 *   t <   3500  → fade out (linear 1→0)
 *   t >=  3500  → 0 (expired)
 *
 * With reduced_motion: hold-only (alpha=1 for the entire 3500ms then 0).
 *
 * @param {number} elapsed_ms
 * @param {boolean} reduced_motion
 * @returns {number} alpha in [0, 1]
 */
export function computeAlpha(elapsed_ms, reduced_motion) {
  if (!(typeof elapsed_ms === 'number' && Number.isFinite(elapsed_ms))) return 0;
  if (elapsed_ms < 0) return 0;
  if (elapsed_ms >= TOTAL_MS) return 0;
  if (reduced_motion) return 1;
  const { fade_in, hold } = PHASES_MS;
  if (elapsed_ms < fade_in) return elapsed_ms / fade_in;
  if (elapsed_ms < fade_in + hold) return 1;
  // Fade-out window.
  const t = elapsed_ms - (fade_in + hold);
  return Math.max(0, 1 - t / PHASES_MS.fade_out);
}

/**
 * @param {number} elapsed_ms
 * @returns {boolean} true once the annotation has fully cleared
 */
export function isExpired(elapsed_ms) {
  return typeof elapsed_ms === 'number' && elapsed_ms >= TOTAL_MS;
}
